import React, { useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import PratusInput from 'components/DesignSystems/New/PratusInput';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from 'components/IAP/hooks/incidentInfoHooks';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import IapAttachmentSelectionList from 'components/IAP/IapAttachmentSelectionList';
import {
  areManifestsEqual,
  IAP_FAMILY_CAPABILITIES,
  resolveIapAttachmentManifest,
  type IapAttachmentManifest,
} from 'components/IAP/iapAttachmentManifest';

type IapAttachmentsEditorProps = {
  disabled?: boolean;
};

/**
 * Which forms and attachments make up this period's IAP.
 *
 * The box it prints to depends on the family: box 6 of the FEMA 202, which
 * carries its own attachment list, or the Coast Guard IAP cover sheet, whose
 * 202 has no such box.
 *
 * The selection is both printed on that cover and used to assemble the
 * bundle, so unchecking a form removes it from the exported document.
 *
 * Saved on commit rather than per toggle: every incident info write appends a
 * new version, and the period resolver only looks back a limited number of
 * versions.
 */
const IapAttachmentsEditor: React.FC<IapAttachmentsEditorProps> = ({
  disabled = false,
}) => {
  const { data: incidentInfo } = useIncidentInfo();
  const incidentMutation = useUpdateIcsInfo();
  const family = useIcsFormFamily();
  const capability = IAP_FAMILY_CAPABILITIES[family];

  const savedManifest = useMemo(
    () =>
      resolveIapAttachmentManifest(incidentInfo?.iapAttachmentManifest, family),
    [incidentInfo?.iapAttachmentManifest, family]
  );

  const [draft, setDraft] = useState<IapAttachmentManifest | null>(null);
  const manifest = draft ?? savedManifest;
  const isDirty = draft !== null && !areManifestsEqual(draft, savedManifest);

  const handleSave = async () => {
    if (!draft) return;
    try {
      await incidentMutation.mutateAsync({
        ...incidentInfo,
        iapAttachmentManifest: draft,
      });
      setDraft(null);
      toast.success('IAP attachments saved.');
    } catch {
      // useUpdateIcsInfo surfaces the failure; keep the draft so edits survive.
    }
  };

  const attachmentLimitReached =
    manifest.otherAttachments.length >= capability.otherAttachmentLimit;

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center mb-2 gap-2">
        <span>Attachments Included in the IAP</span>
        <OverlayTrigger
          placement="top"
          overlay={(props) => (
            <Tooltip {...props}>
              These boxes are printed on the IAP cover and decide which forms
              are merged into the exported IAP. Clearing a form leaves it out of
              the bundle.
            </Tooltip>
          )}
        >
          <span
            className="d-inline-flex align-items-center"
            role="button"
            tabIndex={0}
          >
            <SharedIcon
              iconName="info"
              size="sm"
              color="primary"
              style={{ cursor: 'default' }}
            />
          </span>
        </OverlayTrigger>
      </div>

      <IapAttachmentSelectionList
        manifest={manifest}
        family={family}
        disabled={disabled}
        onChange={setDraft}
      />

      <div className="mt-3">
        <div className="d-flex align-items-center mb-2 gap-2">
          <span>Other Attachments</span>
          <OverlayTrigger
            placement="top"
            overlay={(props) => (
              <Tooltip {...props}>
                Named on the IAP cover so readers know what else travels with
                the plan. These labels are printed only; the files are not
                merged.
              </Tooltip>
            )}
          >
            <span
              className="d-inline-flex align-items-center"
              role="button"
              tabIndex={0}
            >
              <SharedIcon
                iconName="info"
                size="sm"
                color="primary"
                style={{ cursor: 'default' }}
              />
            </span>
          </OverlayTrigger>
        </div>

        {manifest.otherAttachments.map((label, index) => (
          <div
            key={index}
            className="d-flex align-items-end mb-2 gap-2"
            style={{ maxWidth: '32rem' }}
          >
            <div className="flex-grow-1">
              <PratusInput
                label=""
                placeholder="Name an attachment..."
                disabled={disabled}
                value={label}
                onValueChange={(value) => {
                  const next = [...manifest.otherAttachments];
                  next[index] = value;
                  setDraft({ ...manifest, otherAttachments: next });
                }}
              />
            </div>
            <StylishNewButton
              className="button button--secondary"
              disabled={disabled}
              onClick={() =>
                setDraft({
                  ...manifest,
                  otherAttachments: manifest.otherAttachments.filter(
                    (_, i) => i !== index
                  ),
                })
              }
              aria-label={`Remove attachment ${index + 1}`}
            >
              <SharedIcon iconName="delete" size="sm" />
            </StylishNewButton>
          </div>
        ))}

        <StylishNewButton
          className="button button--secondary"
          disabled={disabled || attachmentLimitReached}
          onClick={() =>
            setDraft({
              ...manifest,
              otherAttachments: [...manifest.otherAttachments, ''],
            })
          }
        >
          <SharedIcon iconName="add" size="sm" classes="me-1" />
          Add attachment
        </StylishNewButton>
        {attachmentLimitReached && (
          <div className="mt-1" style={{ fontSize: '0.85rem' }}>
            The {family.toUpperCase()} cover prints at most{' '}
            {capability.otherAttachmentLimit} attachment names.
          </div>
        )}
      </div>

      {isDirty && (
        <div className="d-flex align-items-center mt-3 gap-2">
          <StylishNewButton
            className="button button--primary"
            disabled={disabled || incidentMutation.isPending}
            onClick={() => void handleSave()}
          >
            Save attachments
          </StylishNewButton>
          <StylishNewButton
            className="button button--secondary"
            disabled={incidentMutation.isPending}
            onClick={() => setDraft(null)}
          >
            Discard
          </StylishNewButton>
        </div>
      )}
    </div>
  );
};

export default IapAttachmentsEditor;
